export type AssetStatus = "idle" | "loading" | "loaded" | "error";

/** In-memory image cache. Every URL gets one promise, so repeated requests never refetch. */
export class AssetManager {
  private readonly images = new Map<string, Promise<HTMLImageElement>>();
  private readonly statuses = new Map<string, AssetStatus>();

  loadImage(url: string): Promise<HTMLImageElement> {
    const existing = this.images.get(url);
    if (existing) return existing;

    this.statuses.set(url, "loading");
    const promise = new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.decoding = "async";
      image.onload = () => {
        this.statuses.set(url, "loaded");
        resolve(image);
      };
      image.onerror = () => {
        this.statuses.set(url, "error");
        reject(new Error(`Unable to load image: ${url}`));
      };
      image.src = url;
    });
    this.images.set(url, promise);
    return promise;
  }

  preloadImage(url: string): Promise<HTMLImageElement> {
    return this.loadImage(url);
  }

  isLoaded(url: string): boolean {
    return this.statuses.get(url) === "loaded";
  }

  getStatus(url: string): AssetStatus {
    return this.statuses.get(url) ?? "idle";
  }

  getCachedPromise(url: string): Promise<HTMLImageElement> | undefined {
    return this.images.get(url);
  }
}
